import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { hrApi } from '../../services/hrApi.js';

const RATINGS = [1, 2, 3, 4, 5];
const emptyForm = () => ({ employeeId: '', period: String(new Date().getFullYear()), overallRating: '', strengths: '', improvements: '' });

export function ReviewCreateModal({ open, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(emptyForm);
  const [goals, setGoals] = useState([{ title: '', rating: '' }]);
  const [error, setError] = useState(null);

  const employees = useQuery({ queryKey: ['hr', 'employees'], queryFn: () => hrApi.listEmployees({ limit: 200 }).then((r) => r.items), enabled: open });

  useEffect(() => { if (open) { setForm(emptyForm()); setGoals([{ title: '', rating: '' }]); setError(null); } }, [open]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setGoal = (i, k) => (e) => setGoals((gs) => gs.map((g, idx) => (idx === i ? { ...g, [k]: e.target.value } : g)));

  const save = useMutation({
    mutationFn: (body) => hrApi.createReview(body),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['hr', 'reviews'] }); onClose(); },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  function submit(e) {
    e.preventDefault();
    setError(null);
    if (!form.employeeId || !form.period.trim()) { setError(t('rev.error.required')); return; }
    const cleanGoals = goals.filter((g) => g.title.trim()).map((g) => ({ title: g.title.trim(), rating: g.rating ? Number(g.rating) : null }));
    save.mutate({
      employeeId: form.employeeId,
      period: form.period.trim(),
      overallRating: form.overallRating ? Number(form.overallRating) : null,
      strengths: form.strengths || null,
      improvements: form.improvements || null,
      goals: cleanGoals,
    });
  }

  return (
    <Modal open={open} onClose={onClose} title={t('rev.modal.title')}
      footer={<><Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="primary" onClick={submit} disabled={save.isPending}>{save.isPending ? `${t('common.save')}…` : t('common.save')}</Button></>}>
      <form onSubmit={submit} className="form-stack">
        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('rev.field.employee')}</span>
            <select className="input" value={form.employeeId} onChange={set('employeeId')} required>
              <option value="">-</option>
              {(employees.data || []).map((e) => <option key={e.id} value={e.id}>{e.firstName} {e.lastName}</option>)}
            </select>
          </label>
          <label className="field">
            <span className="field-label">{t('rev.field.period')}</span>
            <input className="input" value={form.period} onChange={set('period')} required />
          </label>
          <label className="field">
            <span className="field-label">{t('rev.field.rating')}</span>
            <select className="input" value={form.overallRating} onChange={set('overallRating')}>
              <option value="">-</option>
              {RATINGS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </label>
        </div>
        <label className="field">
          <span className="field-label">{t('rev.field.strengths')}</span>
          <textarea className="input" rows={2} value={form.strengths} onChange={set('strengths')} />
        </label>
        <label className="field">
          <span className="field-label">{t('rev.field.improvements')}</span>
          <textarea className="input" rows={2} value={form.improvements} onChange={set('improvements')} />
        </label>

        <div className="field">
          <span className="field-label">{t('rev.field.goals')}</span>
          {goals.map((g, i) => (
            <div key={i} className="field-row" style={{ marginBottom: 'var(--space-2)' }}>
              <input className="input" placeholder={t('rev.field.goalTitle')} value={g.title} onChange={setGoal(i, 'title')} />
              <select className="input" style={{ maxWidth: 90 }} value={g.rating} onChange={setGoal(i, 'rating')}>
                <option value="">-</option>
                {RATINGS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          ))}
          <Button type="button" size="sm" variant="ghost" onClick={() => setGoals((gs) => [...gs, { title: '', rating: '' }])}>{t('rev.field.addGoal')}</Button>
        </div>
        {error && <div className="alert danger">{error}</div>}
      </form>
    </Modal>
  );
}
